import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Paperclip, Inbox as InboxIcon } from "lucide-react";
import { api, CATEGORY_COLORS, PRIORITY_META, timeAgo } from "../../lib/api";

const FILTER_KEYS = ["tous", "non_lus", "urgents", "prospects", "clients", "a_traiter", "termines"];

export default function Inbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("tous");
  const [emails, setEmails] = useState(null);
  const q = params.get("q") || "";
  const filterLabels = t("inbox.filters");

  useEffect(() => {
    setEmails(null);
    api.get("/emails", { params: { filter, q: q || undefined } })
      .then((r) => setEmails(r.data.emails)).catch(() => setEmails([]));
  }, [filter, q]);

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tighter">{t("inbox.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {q ? <>{t("inbox.resultsFor")} « <strong className="text-foreground">{q}</strong> » <button onClick={() => setParams({})} className="text-electric hover:underline ml-1" data-testid="clear-search">{t("inbox.clear")}</button></>
               : t("inbox.subtitle")}
          </p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="inbox-filters">
        {FILTER_KEYS.map((key, i) => (
          <button key={key} onClick={() => setFilter(key)} data-testid={`filter-${key}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${filter === key ? "bg-primary text-primary-foreground border-primary dark:bg-electric dark:border-electric" : "border-border bg-card hover:border-electric/50"}`}>
            {filterLabels[i]}
          </button>
        ))}
      </div>

      {!emails ? (
        <div className="space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="skeleton-shimmer h-20 rounded-2xl" />)}</div>
      ) : emails.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card py-20 text-center" data-testid="inbox-empty">
          <InboxIcon size={32} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-semibold text-sm">{t("inbox.empty1")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("inbox.empty2")}</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border/60" data-testid="inbox-list">
          {emails.map((e, i) => (
            <motion.button key={e.email_id} onClick={() => navigate(`/app/inbox/${e.email_id}`)}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.4) }}
              className={`w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-secondary/50 transition-colors ${!e.read ? "bg-electric/[0.03]" : ""}`}
              data-testid={`email-row-${i}`}>
              <span className={`h-2 w-2 rounded-full shrink-0 ${(PRIORITY_META[e.priority] || PRIORITY_META.normale).dot}`} />
              <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-xs font-bold shrink-0">
                {(e.from_name || "?").charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={`text-sm truncate ${!e.read ? "font-bold" : "font-medium"}`}>{e.from_name}</p>
                  {!e.read && <span className="h-1.5 w-1.5 rounded-full bg-electric shrink-0" />}
                  {e.has_attachment && <Paperclip size={12} className="text-muted-foreground shrink-0" />}
                </div>
                <p className="text-xs font-medium truncate mt-0.5">{e.subject}</p>
                <p className="text-xs text-muted-foreground truncate mt-0.5 italic">{e.summary}</p>
              </div>
              <div className="hidden md:flex flex-col items-end gap-1.5 shrink-0">
                <span className={`text-[10px] font-semibold rounded-full px-2.5 py-0.5 ${CATEGORY_COLORS[e.category] || CATEGORY_COLORS.Autre}`}>
                  {e.category}
                </span>
                <span className="text-[10px] text-muted-foreground">{timeAgo(e.date)}</span>
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}
